/**
 * Web Component entry point for the Notifications widget.
 *
 * Importing this module registers the <legion-notifications> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/notifications-web-component';
 *
 * // Then use in HTML:
 * // <legion-notifications base-url="https://your-domain.com" api-key="key" theme="light" />
 * ```
 */

import { attachAuthBridge, type AuthBridgeHandle, AUTH_BRIDGE_OBSERVED_ATTRS } from "../shared/auth-bridge";

const NOTIF_CSS_ATTRIBUTES = [
  "notif-background-color",
  "notif-text-color",
  "notif-secondary-text-color",
  "notif-border-color",
  "notif-border-radius",
  "notif-padding",
  "notif-shadow",
  "notif-primary-color",
  "notif-primary-hover",
  "notif-accent-color",
  "notif-item-bg",
  "notif-item-hover-bg",
  "notif-item-border",
  "notif-item-radius",
  "notif-item-shadow",
  "notif-item-padding",
  "notif-item-gap",
  "notif-unread-bg",
  "notif-unread-border",
  "notif-unread-indicator",
  "notif-avatar-size",
  "notif-avatar-radius",
  "notif-avatar-border",
  "notif-icon-size",
  "notif-icon-color",
  "notif-badge-bg",
  "notif-badge-color",
  "notif-badge-size",
  "notif-font-family",
  "notif-title-size",
  "notif-title-weight",
  "notif-body-size",
  "notif-timestamp-size",
  "notif-empty-icon-color",
  "notif-empty-text-color",
  "notif-empty-icon-size",
  "notif-divider-color",
  "notif-header-bg",
  "notif-header-border",
  "notif-button-bg",
  "notif-button-color",
  "notif-button-radius",
  "notif-transition-duration",
  "notif-hover-scale",
  "notif-loader-color",
] as const;

class LegionNotificationsElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "base-url",
      
      "user-id",
      "theme",
      "width",
      "height",
      "max-notifications",
      "auto-refresh",
      "empty-title",
      "empty-subtitle",
      "accent-color",
      "empty-icon",
      ...NOTIF_CSS_ATTRIBUTES,
    
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get baseUrl(): string {
    return this.getAttribute("base-url") || window.location.origin;
  }

  private get userId(): string | null {
    return this.getAttribute("user-id");
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "100%";
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "400";
  }

  private get maxNotifications(): number {
    return parseInt(this.getAttribute("max-notifications") || "10", 10);
  }

  private get autoRefresh(): boolean {
    return this.getAttribute("auto-refresh") !== "false";
  }

  private get emptyTitle(): string {
    return this.getAttribute("empty-title") || "You're all caught up";
  }

  private get emptySubtitle(): string {
    return (
      this.getAttribute("empty-subtitle") || "No notifications right now"
    );
  }

  private get accentColor(): string {
    return (
      this.getAttribute("accent-color") ||
      (this.theme === "dark" ? "gray" : "blue")
    );
  }

  private get emptyIcon(): string {
    const value = (
      this.getAttribute("empty-icon") || "bell"
    ).toLowerCase();
    return ["bell", "gift", "activity", "trophy"].includes(value)
      ? value
      : "bell";
  }

  render(): void {
    if (!this.shadowRoot) return;

    const url = new URL(`${this.baseUrl}/widget/notifications`);
    url.searchParams.set("theme", this.theme);
    url.searchParams.set("maxNotifications", String(this.maxNotifications));
    url.searchParams.set("autoRefresh", String(this.autoRefresh));
    url.searchParams.set("emptyTitle", this.emptyTitle);
    url.searchParams.set("emptySubtitle", this.emptySubtitle);
    url.searchParams.set("accentColor", this.accentColor);
    url.searchParams.set("emptyIcon", this.emptyIcon);

    if (this.userId) {
      url.searchParams.set("userId", this.userId);
    }

    NOTIF_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    const iframe = document.createElement("iframe");
    iframe.src = url.toString();
    iframe.style.width = this.widgetWidth.includes("%")
      ? this.widgetWidth
      : `${this.widgetWidth}px`;
    iframe.style.height = this.widgetHeight.includes("%")
      ? this.widgetHeight
      : `${this.widgetHeight}px`;
    iframe.frameBorder = "0";
    iframe.scrolling = "auto";
    iframe.title = "Notifications Widget";

    // Shadow DOM styling
    const bgColor =
      this.getAttribute("notif-background-color") ||
      (this.theme === "dark" ? "#1a1a1a" : "white");
    const borderRadius =
      this.getAttribute("notif-border-radius") || "8px";
    const shadow =
      this.getAttribute("notif-shadow") ||
      "0 2px 10px rgba(0, 0, 0, 0.1)";

    const style = document.createElement("style");
    style.textContent = `
      :host { display: block; width: 100%; min-height: ${this.widgetHeight}px; }
      iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: ${borderRadius};
        box-shadow: ${shadow};
        background: ${bgColor};
      }
    `;

    iframe.onload = () => {
      // Send auth token via postMessage
      this.authBridge?.postCurrentToken();
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: { widget: "notifications", theme: this.theme },
        })
      );
    };

    iframe.onerror = () => {
      if (this.shadowRoot) {
        this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load widget</div>`;
      }
    };

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.baseUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-notifications")
) {
  customElements.define("legion-notifications", LegionNotificationsElement);
}

export { LegionNotificationsElement };

